import { createSelector } from '@reduxjs/toolkit';
import maxBy from 'lodash/maxBy';
import * as React from 'react';
import { memo, useCallback, useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  ButtonGroup,
  DialogBody,
  DialogContainer,
  DialogFooter,
  DialogHeader,
  Divider,
  MemberName,
  Message,
  Processing,
} from '@wg/wows-react-uikit';

import { root } from '~/preloaded';
import { isPatrolsEnabled } from '~/helpers/common';
import { t } from '~/helpers/localization';
import useMount from '~/hooks/useMount';
import { getRank, ROLE_NAMES } from '~/roles';
import { ROUTES_MAP } from '~/router/routes';
import { useAppDispatch, useAppSelector } from '~/store';
import { clanMembersIsFetchingSelector } from '~/store/selectors/membersSelectors';
import { actionsClanLeave, clanDisband, clanLeave, forceAccountSync } from '~/Actions/ActionClanLeave';
import { changeCommander, fetchMembers } from '~/Actions/ActionClanProfile';

import { MemberSelect } from '~/UIKit';

import { ClanLeaveConfirmation } from './ClanLeaveConfirmation';
import styles from './ClanLeaveDialog.scss';

import type { AppStateType as RootState } from '~/Reducers';
import type { IAppDispatch } from '~/store';
import type { IClanMember, IDialog } from '~/types/declaration';
import type { Nullish } from '~/types/generics';

enum CLAN_LEAVE_TYPE {
  DEFAULT = 'default',
  DISBAND = 'disband',
  SELECT_COMMANDER = 'selectCommander',
  COMMANDER_WAS_SELECTED = 'commanderWasSelected',
}

const stateSelector = createSelector(
  [
    (state: RootState) => state.clanLeave,
    (state: RootState) => state.currentAccount,
    (state: RootState) => state.members,
  ],
  (clanLeave, currentAccount, clanMembers) => {
    const accountId = currentAccount.id;
    const clanId = currentAccount.clanId;
    const isCommander = currentAccount.roleName === ROLE_NAMES.COMMANDER;

    let members: IClanMember[] = [];
    if (clanMembers && clanId) {
      members = clanMembers.clans[clanId]?.members.filter((item) => item.id !== accountId) ?? [];
    }

    let newCommander: Nullish<IClanMember> = undefined;
    if (isCommander && clanLeave.newCommanderId) {
      newCommander = members.find((member) => member.id === clanLeave.newCommanderId);
    }

    if (isCommander && !newCommander) {
      newCommander = maxBy(members, (member) => getRank(member.roleName));
    }

    return {
      isFetching: clanLeave.isFetching,
      currentAccount,
      newCommander,
      members,
    };
  },
);

type IClanLeaveDialog = IDialog;

const ClanLeaveDialog = ({ hideDialog }: IClanLeaveDialog) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { isFetching, currentAccount, newCommander, members } = useAppSelector(stateSelector, shallowEqual);
  const isMembersFetching = useAppSelector((state) =>
    currentAccount.clanId ? clanMembersIsFetchingSelector(state, currentAccount.clanId) : true,
  );

  const [dialogType, setDialogType] = useState<CLAN_LEAVE_TYPE>(CLAN_LEAVE_TYPE.DEFAULT);
  const [localMembers, setLocalMembers] = useState(members);
  const [localNewCommander, setLocalNewCommander] = useState(newCommander);

  useMount(() => {
    if (!currentAccount.clanId) {
      navigate(`${root}${ROUTES_MAP.ROOT}`, { replace: true });
      return;
    }

    void dispatch(fetchMembers(currentAccount.clanId));
  });

  useEffect(() => {
    if (isMembersFetching) {
      return;
    }

    let type = CLAN_LEAVE_TYPE.DEFAULT;
    if (members.length === 0) {
      type = CLAN_LEAVE_TYPE.DISBAND;
    } else if (newCommander && members.length > 1) {
      type = CLAN_LEAVE_TYPE.SELECT_COMMANDER;
    } else if (newCommander && members.length === 1) {
      type = CLAN_LEAVE_TYPE.COMMANDER_WAS_SELECTED;
    }
    setDialogType(type);
    setLocalMembers(members);
    setLocalNewCommander(newCommander);
  }, [isMembersFetching, members, newCommander]);

  useEffect(() => {
    if (newCommander && localNewCommander !== newCommander) {
      setLocalNewCommander(newCommander);
    }
  }, [newCommander, localNewCommander]);

  const processSuccessLeaveOrDisband = useCallback(
    async (dispatch: IAppDispatch, isLeave: boolean) => {
      dispatch(actionsClanLeave.toggleFetching());

      await dispatch(forceAccountSync(isLeave));

      dispatch(actionsClanLeave.setInitialState());
      dispatch(actionsClanLeave.toggleFetching());

      if (!currentAccount.clanId) {
        navigate(`${root}${ROUTES_MAP.ROOT}`, { replace: true });
      }

      hideDialog();
    },
    [currentAccount.clanId, hideDialog, navigate],
  );

  const onLeaveClanSubmit = useCallback(async () => {
    const result = await dispatch(clanLeave());
    if (result) {
      await processSuccessLeaveOrDisband(dispatch, true);
    }
  }, [dispatch, processSuccessLeaveOrDisband]);

  const onChangeCommander = useCallback(
    async (newCommanderId: number) => {
      dispatch(actionsClanLeave.toggleFetching());

      const result = await dispatch(changeCommander(newCommanderId, true));

      dispatch(actionsClanLeave.toggleFetching());

      if (result) {
        await onLeaveClanSubmit();
      }
    },
    [dispatch, onLeaveClanSubmit],
  );

  const onDisbandClan = useCallback(async () => {
    const result = await dispatch(clanDisband());
    if (result) {
      await processSuccessLeaveOrDisband(dispatch, false);
    }
  }, [dispatch, processSuccessLeaveOrDisband]);

  const onSelectCommander = useCallback(
    (newCommanderId: number) => {
      dispatch(actionsClanLeave.selectCommander(newCommanderId));
    },
    [dispatch],
  );

  const getAction = () => {
    let onActionClick = onLeaveClanSubmit;

    if (dialogType === CLAN_LEAVE_TYPE.DISBAND) {
      onActionClick = onDisbandClan;
    } else if (
      localNewCommander &&
      (dialogType === CLAN_LEAVE_TYPE.SELECT_COMMANDER || dialogType === CLAN_LEAVE_TYPE.COMMANDER_WAS_SELECTED)
    ) {
      onActionClick = () => onChangeCommander(localNewCommander.id);
    }

    return onActionClick;
  };

  const renderBodyContent = () => {
    let bodyContent: React.ReactNode = null;

    if (dialogType === CLAN_LEAVE_TYPE.DISBAND) {
      bodyContent = (
        <div className={styles.container}>
          <div className={styles.inner}>
            <div className={styles.message}>{t('После вашего ухода клан будет распущен')}</div>
          </div>
          <Divider type="major" />
        </div>
      );
    } else if (localNewCommander && dialogType === CLAN_LEAVE_TYPE.SELECT_COMMANDER) {
      bodyContent = (
        <div className={styles.container}>
          <div className={styles.inner}>
            <div className={styles.message}>{t('Новый командующий флотом')}</div>
            <MemberSelect members={localMembers} selected={localNewCommander.id} onChange={onSelectCommander} />
          </div>
          <Divider type="major" />
        </div>
      );
    } else if (localNewCommander && dialogType === CLAN_LEAVE_TYPE.COMMANDER_WAS_SELECTED) {
      bodyContent = (
        <div className={styles.container}>
          <div className={styles.inner}>
            <div className={styles.message}>{t('Новый командующий флотом')}</div>
            <MemberName role={localNewCommander.roleName} name={localNewCommander.name} />
          </div>
          <Divider type="major" />
        </div>
      );
    }

    return bodyContent;
  };

  const buttonActionText = dialogType === CLAN_LEAVE_TYPE.DISBAND ? t('Распустить клан') : t('Покинуть клан');
  const onActionClick = getAction();

  if (isMembersFetching) {
    return <Processing isFetching className={styles.processing} />;
  }

  return (
    <DialogContainer>
      <DialogHeader>{t('Покинуть клан')}</DialogHeader>
      <DialogBody>
        {renderBodyContent()}
        <div className={styles.confirmation}>{t('Вы уверены, что хотите покинуть клан?')}</div>
        <Divider type="major" />
        <div className={styles.info}>
          <Message type="error" style={'inline'}>
            {t('Заработанные в этом клане баррели нефти обнуляются. В новом клане вы начнёте зарабатывать их заново.')}
          </Message>
        </div>
        <ClanLeaveConfirmation />
        {isPatrolsEnabled() ? (
          <div className={styles.confirmation}>
            {t('All unclaimed rewards earned in Clan Patrols will be credited to your account automatically.')}
          </div>
        ) : null}
      </DialogBody>
      <DialogFooter>
        <ButtonGroup isLoading={isFetching}>
          <Button isJustified isFlat onClick={() => void onActionClick()}>
            {buttonActionText}
          </Button>
          <Button isJustified isFlat onClick={hideDialog}>
            {t('Отмена')}
          </Button>
        </ButtonGroup>
      </DialogFooter>
    </DialogContainer>
  );
};

export default memo(ClanLeaveDialog);
